const MESSENGER_FRAME_SELECTOR = '.intercom-messenger-frame';

/** Minimal visual viewport shrink (px) that is treated as an open keyboard. */
const KEYBOARD_MIN_HEIGHT = 100;

/** Re-sync period (ms) while the messenger is open or the viewport is shrunk. */
const RESYNC_INTERVAL = 300;

const EDITABLE_SELECTOR =
  'input, textarea, [contenteditable]:not([contenteditable="false"])';

/**
 * Keeps the Intercom messenger composer visible above the on-screen keyboard
 * (JS-13813). iOS ignores `interactive-widget=resizes-content` and only
 * shrinks the visual viewport, leaving the fixed fullscreen messenger frame
 * (and its composer) behind the keyboard — so the frame is clamped to the
 * visual viewport manually. Inline `!important` is required to beat
 * Intercom's injected stylesheet; see also `src/styles/_intercom.scss`.
 */
export const initIntercomKeyboardFix = () => {
  const visualViewport = window.visualViewport;
  if (!visualViewport) return;

  let resyncTimer: number | undefined;
  let stableWidth = 0;
  let stableHeight = 0;

  /**
   * An on-screen keyboard always has a focused editable behind it. Walks into
   * same-origin frames (Intercom's composer lives in one); a frame that cannot
   * be inspected is trusted to hold one.
   */
  const isEditableFocused = () => {
    if (!document.hasFocus()) return false;
    let element: Element | null = document.activeElement;
    while (element?.tagName === 'IFRAME') {
      const inner = (element as HTMLIFrameElement).contentDocument;
      if (!inner) return true;
      element = inner.activeElement;
    }
    // No instanceof: the element may come from the frame's realm.
    return element?.matches(EDITABLE_SELECTOR) ?? false;
  };

  const sync = () => {
    window.clearTimeout(resyncTimer);

    const frame = document.querySelector<HTMLElement>(MESSENGER_FRAME_SELECTOR);
    const isFullscreenFrame =
      frame !== null && frame.offsetWidth === window.innerWidth;
    // Measured against the tallest layout viewport seen in this orientation:
    // while WebKit reveals the focused composer it shrinks `innerHeight` to the
    // visual viewport as well, so comparing the two would read as no keyboard.
    if (window.innerWidth !== stableWidth) {
      stableWidth = window.innerWidth;
      stableHeight = 0;
    }
    stableHeight = Math.max(stableHeight, window.innerHeight);
    const keyboardHeight = stableHeight - visualViewport.height;
    const isViewportShrunk =
      visualViewport.scale === 1 && keyboardHeight > KEYBOARD_MIN_HEIGHT;
    // A shrunk visual viewport alone is not proof of a keyboard: after a
    // gesture-driven dismissal (back-swipe, reply popup) WKWebView can leave
    // it stale for good, and trusting it would keep the messenger clamped to
    // half the screen with no keyboard in sight.
    const isKeyboardOpen = isViewportShrunk && isEditableFocused();

    // Poll while something can still change without an event: Intercom mounts
    // the frame with the keyboard already up (reply popup), WebKit settles a
    // displaced scroll offset late, a transient reading flips the state.
    if (isViewportShrunk || isFullscreenFrame) {
      resyncTimer = window.setTimeout(sync, RESYNC_INTERVAL);
    }
    if (!frame) return;

    const container = frame.closest<HTMLElement>('#intercom-container');

    if (isKeyboardOpen && isFullscreenFrame) {
      // Fixed boxes follow the layout viewport, so once WebKit has scrolled
      // the page past its end to reveal the composer they sit `offsetTop`
      // above the visible area; `getBoundingClientRect` reports visual
      // coordinates and confirms it (verified on iOS 26 too).
      const shift = visualViewport.offsetTop;
      frame.style.setProperty(
        'top',
        `calc(${shift}px + env(safe-area-inset-top, 0px))`,
        'important',
      );
      frame.style.setProperty(
        'height',
        `calc(${visualViewport.height}px - env(safe-area-inset-top, 0px))`,
        'important',
      );
      container?.style.setProperty('--intercom-fixed-shift', `${shift}px`);
      return;
    }

    frame.style.removeProperty('top');
    frame.style.removeProperty('height');
    container?.style.removeProperty('--intercom-fixed-shift');

    // WKWebView can leave the page over-scrolled after a gesture-driven
    // keyboard dismissal, which drags the fixed messenger header off-screen
    // on iOS ≤ 18 and keeps the stale viewport above from ever updating.
    // Same repair as react-native-webview's keyboardDisplacementFix, but
    // race-free: a no-op for in-range scroll.
    const maxScroll = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );
    const overScroll =
      Math.max(window.scrollY, visualViewport.pageTop) - maxScroll;
    if (isFullscreenFrame && overScroll > 1) {
      window.scrollTo(0, maxScroll);
    }
  };

  visualViewport.addEventListener('resize', sync);
  visualViewport.addEventListener('scroll', sync);
  window.addEventListener('resize', sync);
  window.addEventListener('scroll', sync, { passive: true });
  window.addEventListener('popstate', sync);
  window.addEventListener('pageshow', sync);
  sync();
};
